import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@omni/ui/components/Button/button.component";
import { Card } from "@omni/ui/components/Card/card";
import * as SessionStorage from "@omni/utils/sessionStorage.utils";
import { cn } from "@omni/utils/styles";
import React, { useEffect } from "react";
import { FormProvider, useFormState } from "react-hook-form";

import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Row } from "@/components/General/Grid/row.component";
import { HasAccountModal } from "@/components/Modals/HasAccountModal/hasAccountModal";
import { APIStatus } from "@/redux/modules/api/data/checkUser/checkUser.module";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import * as SoftLoginService from "@/services/softLogin.service";
import { StorageKeys } from "@/utils/enums";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { useIsClientSide } from "@/utils/hooks/isClient.hook";
import { useModalRouter } from "@/utils/hooks/useModalRouter";

import {
    CheckoutRegisterFormFormValidationSchema,
    CheckoutRegisterFormFormValues
} from "./registerCheckoutForm.schema";

const FORM_ID = "guestForm";

type CheckoutRegisterFormProps = {
    title: string;
    buttonText: string;
    notification: string;
    privacypolicy: string;
    isOpen: boolean;
    onSubmit: (email: string) => void;
    createSubmitHandler: (
        formHandler: (e?: React.BaseSyntheticEvent) => Promise<void>
    ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
    updateCardToggle: (isOpen: boolean) => void;
    isLoading: boolean;
    load: (email: string) => Promise<TApiAction<any>>;
};

type SubmitButtonProps = {
    initialized: boolean;
    isLoading: boolean;
    buttonText: string;
};

const SubmitButton = ({
    initialized,
    isLoading,
    buttonText
}: SubmitButtonProps) => {
    const { isSubmitting, isValidating, isValid } = useFormState();
    return (
        <Button
            test="registerCheckoutForm-button-submit"
            type="submit"
            size="lg"
            loading={isSubmitting && isLoading}
            disabled={isValidating || !isValid || !initialized}
        >
            {buttonText}
        </Button>
    );
};

const CheckoutRegisterForm = ({
    title,
    buttonText,
    notification,
    privacypolicy,
    isOpen,
    onSubmit,
    createSubmitHandler,
    updateCardToggle,
    isLoading,
    load
}: CheckoutRegisterFormProps) => {
    const { t } = useGranite();
    const isClientSide = useIsClientSide();

    const { open: openModal } = useModalRouter(ModalType.HasAccount);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            guestEmail: ""
        },
        resolver: yupResolver(CheckoutRegisterFormFormValidationSchema)
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (isClientSide) {
            const storedEmail = SessionStorage.getItem(StorageKeys.GuestEmail);
            if (storedEmail) {
                methods.setValue("guestEmail", storedEmail);
            }
        }
    }, [isClientSide]);

    const onFormSubmit = ({ guestEmail }: CheckoutRegisterFormFormValues) => {
        SessionStorage.setItem(StorageKeys.GuestEmail, guestEmail);

        return load(guestEmail).then((action) => {
            if (action.type === APIStatus.SUCCESS) {
                openModal();
            } else {
                SoftLoginService.clearSoftLogin();
                if (onSubmit) {
                    onSubmit(guestEmail);
                }
            }
        });
    };

    const handleCardToggle = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        updateCardToggle(!isOpen);
    };

    return (
        <Card className={cn("tw:mb-0!")}>
            <Card.Header
                className={cn("tw:px-5 tw:text-xl tw:md:hidden tw:border-0!")}
            >
                <a
                    href="#"
                    onClick={handleCardToggle}
                    className={cn("tw:md:hidden tw:m-3")}
                >
                    <Row>
                        <div className={cn("col-9 tw:m-0 tw:py-0")}>
                            <h3 className={cn("tw:m-0!")}>{title}</h3>
                        </div>
                        <div className={cn("col-3 tw:text-right")}>
                            <i
                                className={`las la-angle-${
                                    isOpen ? "down" : "right"
                                }`}
                            />
                        </div>
                    </Row>
                </a>
            </Card.Header>
            <div
                className={cn(
                    "tw:h-full",
                    "tw:md:block collapse",
                    isOpen && "show"
                )}
            >
                <FormInfoProvider
                    formId={FORM_ID}
                    withNeverbounce={true}
                >
                    {({ initialized }) => {
                        return (
                            <FormProvider {...methods}>
                                <form
                                    id={FORM_ID}
                                    className={cn(
                                        "tw:h-full tw:flex tw:flex-col tw:justify-end"
                                    )}
                                    onSubmit={createSubmitHandler(
                                        methods.handleSubmit(onFormSubmit)
                                    )}
                                >
                                    <Card.Body>
                                        <h2 className="d-none d-md-block">
                                            {title}
                                        </h2>
                                        <TextField<CheckoutRegisterFormFormValues>
                                            test="register-input-guestEmail"
                                            name="guestEmail"
                                            label={t(
                                                "general.form.email.label"
                                            )}
                                            required
                                            autoComplete={"email"}
                                            disabled={!initialized}
                                        />
                                        <div>
                                            <p>{notification}</p>
                                            <p>
                                                <a href={privacypolicy}>
                                                    {t(
                                                        "registerCheckoutForm.link.privacyPolicy"
                                                    )}
                                                </a>
                                            </p>
                                        </div>
                                    </Card.Body>
                                    <Card.Footer
                                        className={cn(
                                            "tw:border-0 tw:px-5 tw:pb-5 tw:pt-0"
                                        )}
                                        style={{ backgroundColor: "inherit" }}
                                    >
                                        <SubmitButton
                                            initialized={initialized}
                                            isLoading={isLoading}
                                            buttonText={buttonText}
                                        />
                                    </Card.Footer>
                                </form>
                            </FormProvider>
                        );
                    }}
                </FormInfoProvider>
            </div>
            <HasAccountModal />
        </Card>
    );
};

export default CheckoutRegisterForm;
